import { drawSpatialFieldRgb } from '../canvasUtils';

const EMPTY = 0;
const GROUP_A = 1;
const GROUP_B = 2;

function buildEmptyList(grid, n) {
    const empty = [];
    for (let i = 0; i < n * n; i++) {
        if (grid[i] === EMPTY) empty.push(i);
    }
    return empty;
}

function neighborFractionSame(grid, n, idx, group) {
    const x = idx % n;
    const y = Math.floor(idx / n);
    let same = 0;
    let total = 0;
    for (let dy = -1; dy <= 1; dy++) {
        for (let dx = -1; dx <= 1; dx++) {
            if (dx === 0 && dy === 0) continue;
            const nx = (x + dx + n) % n;
            const ny = (y + dy + n) % n;
            const ni = ny * n + nx;
            if (grid[ni] !== EMPTY) {
                total += 1;
                if (grid[ni] === group) same += 1;
            }
        }
    }
    if (total === 0) return 1;
    return same / total;
}

function initSegregation(n, params) {
    const grid = new Uint8Array(n * n);
    for (let i = 0; i < n * n; i++) {
        if (Math.random() < params.density) {
            grid[i] = Math.random() < params.minorityShare ? GROUP_B : GROUP_A;
        } else {
            grid[i] = EMPTY;
        }
    }
    return {
        n,
        grid,
        empty: buildEmptyList(grid, n),
        t: 0,
        moves: 0,
    };
}

function stepSegregation(state, params) {
    const { n, grid } = state;
    const threshold = params.similarityThreshold;
    const unhappy = [];

    for (let i = 0; i < n * n; i++) {
        if (grid[i] === EMPTY) continue;
        if (neighborFractionSame(grid, n, i, grid[i]) < threshold) {
            unhappy.push(i);
        }
    }

    const empty = buildEmptyList(grid, n);
    for (let k = 0; k < unhappy.length; k++) {
        const idx = unhappy[k];
        if (grid[idx] === EMPTY || empty.length === 0) continue;

        const pick = Math.floor(Math.random() * empty.length);
        const target = empty[pick];
        grid[target] = grid[idx];
        grid[idx] = EMPTY;
        empty[pick] = empty[empty.length - 1];
        empty.pop();
        empty.push(idx);
        state.moves += 1;
    }

    state.empty = buildEmptyList(grid, n);
    state.t += 1;
}

function homogeneity(grid, n) {
    let sum = 0;
    let count = 0;
    for (let i = 0; i < n * n; i++) {
        if (grid[i] === EMPTY) continue;
        sum += neighborFractionSame(grid, n, i, grid[i]);
        count += 1;
    }
    return count === 0 ? 0 : sum / count;
}

export const segregationABM = {
    init: (w, h, params) => initSegregation(56, params),
    step: (ctx, w, h, state, params) => {
        for (let i = 0; i < params.stepsPerFrame; i++) {
            stepSegregation(state, params);
        }

        const { n, grid } = state;
        const homog = homogeneity(grid, n);

        drawSpatialFieldRgb(ctx, w, h, n, (idx) => {
            if (grid[idx] === GROUP_A) return '#ef4743';
            if (grid[idx] === GROUP_B) return '#4fc3f7';
            return '#1a1a1a';
        }, {
            xLabel: 'x (grid)',
            yLabel: 'y (grid)',
            title: `Schelling segregation | homogeneity ${(homog * 100).toFixed(1)}%`,
            t: state.t,
        });
    },
};

export const segregationMetrics = {
    init: (w, h, params) => {
        const base = initSegregation(56, params);
        base.history = [];
        return base;
    },
    step: (ctx, w, h, state, params) => {
        for (let i = 0; i < params.stepsPerFrame; i++) {
            stepSegregation(state, params);
        }
        const homog = homogeneity(state.grid, state.n);
        state.history.push({ t: state.t, homog });
        if (state.history.length > 500) state.history.shift();

        const { n, grid } = state;
        drawSpatialFieldRgb(ctx, w, h, n, (idx) => {
            if (grid[idx] === GROUP_A) return '#ef4743';
            if (grid[idx] === GROUP_B) return '#4fc3f7';
            return '#1a1a1a';
        }, {
            xLabel: 'x (grid)',
            yLabel: 'y (grid)',
            title: `Emergent clusters | moves ${state.moves}`,
            t: state.t,
        });
    },
};
